import {PayloadAction} from '@reduxjs/toolkit';
import {GatewayError} from '@yandex-data-ui/gateway';

import {OrderDirection} from '~shared/models';
import {APICollectionResult} from '~shared/types';

import {BasePanelState} from './types';

export const init = <Item, Filters extends object>(state: BasePanelState<Item, Filters>) => {
    state.loading = true;
    state.isFirstPage = true;
    state.nextCursor = null;
    state.error = undefined;
};

export const makeReset =
    <Item, Filters extends object>(initialState: BasePanelState<Item, Filters>) =>
    (state: BasePanelState<Item, Filters>) => {
        Object.assign(state, initialState);
    };

export const loadNextPage = <Item, Filters extends object>(
    state: BasePanelState<Item, Filters>,
) => {
    state.isFirstPage = false;
    state.loadingMore = true;
};

export const setLoading = <Item, Filters extends object>(
    state: BasePanelState<Item, Filters>,
    action: PayloadAction<boolean>,
) => {
    state.loading = action.payload;
};

export const setLoadingMore = <Item, Filters extends object>(
    state: BasePanelState<Item, Filters>,
    action: PayloadAction<boolean>,
) => {
    state.loadingMore = action.payload;
};

export const setError = <Item, Filters extends object>(
    state: BasePanelState<Item, Filters>,
    action: PayloadAction<GatewayError>,
) => {
    state.error = action.payload;
};

export const setQueryFilter = <Item, Filters extends object>(
    state: BasePanelState<Item, Filters>,
    action: PayloadAction<string>,
) => {
    state.loading = true;
    state.isFirstPage = true;
    state.nextCursor = null;
    state.filters.query = action.payload;
};

export const makeChangeOrder =
    (defaultOrderBy: string) =>
    <Item, Filters extends object>(
        state: BasePanelState<Item, Filters>,
        action: PayloadAction<{
            orderBy: string | null;
            orderDirection: OrderDirection;
        }>,
    ) => {
        state.loading = true;
        state.isFirstPage = true;
        state.nextCursor = null;
        state.orderBy = action.payload.orderBy || defaultOrderBy;
        state.orderDirection = action.payload.orderDirection;
    };

export const makeSetItems =
    <ServerItem, TableItem>(adapter: (item: ServerItem) => TableItem) =>
    <Filters extends object>(
        state: BasePanelState<TableItem, Filters>,
        action: PayloadAction<APICollectionResult<ServerItem>>,
    ) => {
        const {results, next_cursor: nextCursor} = action.payload;

        state.items = results.map(adapter);
        state.nextCursor = nextCursor;
        state.isFirstPage = false;

        if (state.items.length > 0) {
            state.isEmpty = false;
        }
    };

export const makeAddItems =
    <ServerItem, TableItem>(adapter: (item: ServerItem) => TableItem) =>
    <Filters extends object>(
        state: BasePanelState<TableItem, Filters>,
        action: PayloadAction<APICollectionResult<ServerItem>>,
    ) => {
        const {results, next_cursor: nextCursor} = action.payload;

        state.items.push(...results.map(adapter));
        state.nextCursor = nextCursor;
        state.isFirstPage = false;
        state.error = undefined;

        if (state.items.length > 0) {
            state.isEmpty = false;
        }
    };
